import {
  BarChart,
  BarChartCoordinates,
  BarChartDataset,
  BarChartProps,
  BarChartRef,
  CenteredLoader,
  Flex,
  useTheme,
} from "@shared/primitive-components"
import { ChartLegend, ChartLegendItem } from "@shared/primitive-components/src/Charts/ChartLegend/ChartLegend"
import { MouseEvent, useMemo } from "react"
import { getElementAtEvent } from "react-chartjs-2"
import { inDateTimeRange, reduceDateByOneHour, toPeriodLabel } from "../../../../utils"
import { SpeedingComponentProps } from "../../../types"

export const SPEEDING_CHART_DTI = `speeding_chart`

type Data = BarChartCoordinates<number>[]
type Labels = string[]

export const SpeedingChart = ({ periods, activePeriod, setActivePeriod, loading }: SpeedingComponentProps) => {
  const theme = useTheme()
  const xAxisTickColor = useMemo(
    () =>
      periods.map((p) =>
        activePeriod
          ? activePeriod.number === p.number
            ? theme.palette.full.blue
            : theme.palette.coal.light
          : inDateTimeRange(p.startedAt, reduceDateByOneHour(p.endedAt))
          ? theme.palette.full.blue
          : theme.palette.coal.light
      ),
    [activePeriod, periods, theme]
  )

  const chart = useMemo<BarChartProps<Data, Labels>>(() => {
    const labels = periods.map((period) => toPeriodLabel(period.startedAt, reduceDateByOneHour(period.endedAt)))

    const periodsDatesets = periods.map((it, i) => ({
      x: labels[i],
      y: typeof it.analytics?.distanceSpentSpeeding === `number` ? it.analytics.distanceSpentSpeeding * 100 : 0,
    }))

    const periodsOffsetArray = periods.map((it, i) => ({
      x: labels[i],
      y: typeof it.analytics?.distanceSpentSpeeding === `number` ? 100 - it.analytics.distanceSpentSpeeding * 100 : 0,
    }))

    const datasets: BarChartDataset<Data> = [
      {
        data: periodsDatesets,
        datasetOptions: {
          backgroundColor: theme.palette.full.blue,
          hoverBackgroundColor: theme.palette.full.darkBlue,
          barThickness: `flex`,
          barPercentage: 0.95,
          categoryPercentage: 1,
        },
      },
      {
        data: periodsOffsetArray,
        datasetOptions: {
          backgroundColor: theme.palette.grey.light,
          barThickness: `flex`,
          barPercentage: 0.95,
          categoryPercentage: 1,
        },
      },
    ]

    const clickHandler = (chartCurrent: BarChartRef<Data, Labels>, e: MouseEvent<HTMLCanvasElement>) => {
      if (!chartCurrent) return
      // Chart.js typing reaching it's limit!!
      // @ts-ignore
      const element = getElementAtEvent(chartCurrent, e)

      if (element.length > 0) {
        setActivePeriod(periods[element[0].index])
      }
    }

    return {
      clickHandler,
      datasets,
      height: 300,
      labels,
      maintainAspectRatio: false,
      title: `% of miles spent speeding`,
      toolTipEnabled: false,
      xAxisTickColor,
      yAxisMaxValue: 100,
      yAxisStepSize: 25,
    }
  }, [periods, setActivePeriod, theme, xAxisTickColor])

  if (loading) return <CenteredLoader height="calc(100% - 80px)" />

  if (periods.length > 0 && activePeriod === null) {
    const period = periods.find((p) => inDateTimeRange(p.startedAt, p.endedAt))
    setActivePeriod(period && period.analytics !== null ? period : periods[0])
  }

  return (
    <Flex flexDirection="column">
      {activePeriod !== null && (
        <ChartLegend>
          <ChartLegendItem label="Current policy's speeding" color={theme.palette.full.blue} />
          <ChartLegendItem label="No speeding" color={theme.palette.grey.light} />
        </ChartLegend>
      )}
      <BarChart<Data, Labels> chart={chart} dataTestId={SPEEDING_CHART_DTI} />
    </Flex>
  )
}
